<template>
  <div class="launchpad">
    <section class="panel">
      <div class="panel-head">
        <h1>配置助手</h1>
        <p class="subhead">识别设备 · 选择固件 · 在线烧录 · 参数配置</p>
        <p class="lead">输入 8 位设备 ID 获取硬件信息，选择固件与版本在线烧录，已烧录设备可直接配置。</p>
        <p class="note">该版本配置助手与原 fishbot_tool 配置工具不通用。</p>
      </div>

      <div class="stepper" aria-label="steps">
        <button class="stepper-item" :class="{ on: activeStep === 1 }" @click="activeStep = 1">1 识别</button>
        <button class="stepper-item" :class="{ on: activeStep === 2 }" @click="activeStep = 2" :disabled="!deviceInfo">2 固件</button>
        <button class="stepper-item" :class="{ on: activeStep === 3 }" @click="activeStep = 3" :disabled="flashState === 'idle'">3 烧录</button>
        <button class="stepper-item" :class="{ on: activeStep === 4 }" @click="activeStep = 4">4 配置</button>
      </div>

      <div class="step-wrap">
        <el-card class="card step-card" shadow="never" v-show="activeStep === 1">
          <div class="card-title">1) 输入设备 ID</div>
          <div class="field-label">设备 ID（8 位数字）</div>
          <div class="device-id-row">
            <template v-for="(digit, index) in deviceIdDigits">
              <input
                :key="`digit-${index}`"
                ref="deviceDigit"
                class="digit-input"
                type="text"
                inputmode="numeric"
                maxlength="1"
                :value="digit"
                :disabled="flashing"
                @input="onDeviceIdDigitInput(index, $event)"
                @keydown="onDeviceIdDigitKeydown(index, $event)"
                @paste.prevent
              />
              <span v-if="index === 3" class="digit-sep">_</span>
            </template>
          </div>
          <div class="field-help" :class="{ ok: isDeviceIdValid, bad: deviceId.length > 0 && !isDeviceIdValid }">
            {{ deviceIdHelpText }}
          </div>

          <div class="step-subtitle">识别结果</div>
          <div class="result" v-if="deviceInfo">
            <div class="result-head">
              <div class="result-title">识别成功</div>
            </div>
            <div class="result-grid">
              <div class="result-item">
                <div class="result-label">设备 ID</div>
                <div class="result-value mono">{{ deviceInfo.deviceId }}</div>
              </div>
              <div class="result-item">
                <div class="result-label">设备类型</div>
                <div class="result-value">{{ deviceInfo.deviceType || '-' }}</div>
              </div>
              <div class="result-item">
                <div class="result-label">硬件版本</div>
                <div class="result-value">{{ deviceInfo.hardwareVersion || '-' }}</div>
              </div>
              <div class="result-item">
                <div class="result-label">芯片类型</div>
                <div class="result-value">{{ deviceInfo.chipFamily || '-' }}</div>
              </div>
              <div class="result-item">
                <div class="result-label">型号</div>
                <div class="result-value">{{ deviceInfo.model || '-' }}</div>
              </div>
            </div>

            <div class="card-actions" style="margin-top: 12px;">
              <el-button type="primary" @click="activeStep = 2">下一步</el-button>
            </div>
          </div>
          <div class="empty" v-else>还未查询硬件信息。</div>
        </el-card>

        <el-card class="card step-card" shadow="never" v-show="activeStep === 2">
          <div class="card-title">2) 选择固件类型与版本</div>
          <div class="fw-picker" style="margin-top: 12px;">
            <div class="fw-picker-row">
              <div class="field">
                <div class="field-label">类型</div>
                <el-select
                  v-model="selectedFirmwareType"
                  placeholder="选择类型"
                  :disabled="firmwareTypes.length === 0"
                  @change="onFirmwareTypeChange"
                >
                  <el-option v-for="type in firmwareTypes" :key="type" :label="type" :value="type" />
                </el-select>
              </div>
              <div class="field">
                <div class="field-label">版本</div>
                <el-select
                  v-model="selectedFirmwareKey"
                  placeholder="选择版本"
                  :disabled="!selectedFirmwareType"
                >
                  <el-option v-for="fw in firmwareOptions" :key="fw._key" :label="firmwareOptionLabel(fw)" :value="fw._key" />
                </el-select>
              </div>
              <div class="fw-picker-actions">
                <el-button @click="activeStep = 4">配置</el-button>
                <el-button
                  type="primary"
                  :loading="flashRunning"
                  :disabled="!selectedFirmware || !selectedFirmwareHasFile || !selectedFirmwareDownloadUrl || flashRunning"
                  @click="startFlash(selectedFirmware)"
                >烧录</el-button>
              </div>
            </div>
            <div class="fw-detail" v-if="selectedFirmware">
              <div class="fw-title">
                <b>{{ selectedFirmware.title || selectedFirmware.name || '未命名固件' }}</b>
                <span class="fw-badge" v-if="selectedFirmware.isLatest">latest</span>
              </div>
            <div class="fw-sub">
              版本：{{ selectedFirmware.version || '-' }} ｜ 支持硬件：{{ formatHardwareVersions(selectedFirmware) }} ｜ 芯片：{{ selectedFirmware.chipFamily || '-' }}
              <span v-if="selectedFirmwareHasFile === false"> ｜ 固件文件缺失</span>
            </div>
              <div class="fw-notes" v-if="selectedFirmware.releaseNotes">{{ selectedFirmware.releaseNotes }}</div>
            </div>
            <div class="empty" v-else>请先选择类型和版本。</div>
          </div>
        </el-card>

        <el-card class="card step-card" shadow="never" v-show="activeStep === 3">
          <div class="card-title">3) 烧录</div>
          <div class="flash-status">
            <span class="dot" :class="{ on: flashState !== 'idle' }"></span>
            <div class="status-text">
              <div class="status-title">{{ flashStatusTitle }} <span v-if="flashProgressText">{{ flashProgressText }}</span></div>
              <div class="status-sub">{{ flashStatusSub }}</div>
            </div>
          </div>
          <div class="log" style="margin-top: 12px;">
            <div class="log-head">
              <div>
                <b>进度日志</b>
                <span class="log-sub">烧录过程中请保持页面打开</span>
              </div>
              <el-button size="mini" :disabled="logLines.length === 0" @click="clearLog">清空</el-button>
            </div>
            <pre ref="flashLogBody" class="log-body">{{ logText }}</pre>
          </div>

          <div class="card-actions" v-if="flashState === 'success'">
            <el-button type="primary" @click="activeStep = 4">去配置</el-button>
          </div>
        </el-card>

        <el-card class="card step-card" shadow="never" v-show="activeStep === 4">
          <div class="card-title">4) 配置</div>
          <div class="status-row">
            <span class="dot" :class="{ on: isConnected }"></span>
            <div class="status-text">
              <div class="status-title">{{ isConnected ? '已连接设备' : '未连接' }}</div>
              <div class="status-sub" v-if="isConnected && isPortOpen">串口已打开</div>
              <div class="status-sub" v-else-if="isConnected">已授权串口（未打开）</div>
              <div class="status-sub" v-else>先点击“连接设备”并授权串口访问</div>
            </div>
          </div>
          <div class="status-actions">
            <el-button type="primary" :disabled="connecting || isConnected" :loading="connecting" @click="connectDevice">连接设备</el-button>
            <el-button :disabled="connecting || !isConnected" @click="disconnectDevice">断开连接</el-button>
          </div>
          <div class="status-warn" v-if="serialSupported === false">
            当前环境未检测到 Web Serial。{{ serialSupportHint || '可尝试切换到 HTTPS/localhost 或检查浏览器实验特性。' }}
          </div>
          <div class="card-actions">
            <el-button type="primary" :loading="configLoading" :disabled="!isConnected" @click="readConfig">扫描配置</el-button>
            <el-button :loading="restartLoading" :disabled="!isConnected || configLoading" @click="sendRestart">重启</el-button>
          </div>

          <div class="log" style="margin-top: 12px;" v-if="configDebugLines.length">
            <div class="log-head">
              <div>
                <b>调试日志</b>
                <span class="log-sub">收发数据（TX/RX）</span>
              </div>
              <el-button size="mini" :disabled="configDebugLines.length === 0" @click="clearConfigDebug">清空</el-button>
            </div>
            <pre ref="configLogBody" class="log-body">{{ configDebugText }}</pre>
          </div>
          <div class="config-groups" v-if="groupedConfigItems.length">
            <div class="config-group" v-for="group in groupedConfigItems" :key="group.key">
              <div class="config-group-title">{{ group.title }}</div>
              <div class="config-list">
                <div class="config-item" v-for="item in group.items" :key="item.name">
                  <div class="config-name">{{ item.name }}</div>
                  <el-input v-model="item.value" :disabled="item.readOnly" />
                  <div class="config-actions">
                    <span v-if="item.readOnly" class="ro">只读</span>
                    <el-button v-else size="mini" :loading="item.writing" @click="writeConfigItem(item)">写入</el-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="empty" v-else>
            {{ configEmptyText }}
          </div>
        </el-card>
      </div>
    </section>
  </div>
</template>

<script>
import { getFirmwareListByDeviceId, getLatestFirmware, getWmsDeviceHardware } from '@/server.js'

export default {
  data () {
    return {
      activeStep: 1,
      serialSupported: null,
      serialSupportHint: '',
      serialFilters: [
        { usbVendorId: 0x1a86, usbProductId: 0x7522 },
        { usbVendorId: 0x1a86, usbProductId: 0x7523 },
        { usbVendorId: 0x303a, usbProductId: 0x1001 },
        { usbVendorId: 0x1a86, usbProductId: 0x55d3 }
      ],

      port: null,
      connecting: false,

      flashingBaudrate: 115200,
      consoleBaudrate: 115200,

      deviceId: '',
      deviceIdDigits: ['', '', '', '', '', '', '', ''],
      deviceInfo: null,
      firmwareInfo: null,
      firmwareList: [],
      selectedFirmwareType: '',
      selectedFirmwareKey: '',
      lastAutoDeviceId: '',
      autoFetching: false,
      flashState: 'idle',
      flashStateDetail: '',
      flashProgress: 0,
      flashRunning: false,
      esptoolModule: null,
      errorHandlerInstalled: false,

      configItems: [],
      configLoading: false,
      restartLoading: false,
      configDebugLines: [],

      loadingDevice: false,
      loadingFirmware: false,
      flashing: false,

      logLines: [],

      consoleStarting: false,
      consoleRunning: false,
      consoleLines: [],
      consoleReader: null,
      consolePipeAborter: null
    }
  },
  computed: {
    isConnected () {
      return !!this.port
    },
    isPortOpen () {
      return !!(this.port && this.port.readable && this.port.writable)
    },
    isDeviceIdValid () {
      return /^\d{8}$/.test(this.deviceId)
    },
    deviceIdHelpText () {
      if (this.deviceId.length === 0) return '请输入 8 位数字设备 ID'
      if (this.isDeviceIdValid) return '格式正确'
      return '仅支持 8 位数字'
    },
    deviceHardwareVersion () {
      return (this.deviceInfo && this.deviceInfo.hardwareVersion) ? this.deviceInfo.hardwareVersion : ''
    },
    logText () {
      return this.logLines.join('\n')
    },
    consoleText () {
      return this.consoleLines.join('')
    },
    firmwareTypes () {
      const types = []
      this.firmwareList.forEach((fw) => {
        const type = fw.firmwareType || '未分类'
        if (!types.includes(type)) types.push(type)
      })
      return types.sort((a, b) => String(a).localeCompare(String(b)))
    },
    firmwareOptions () {
      if (!this.selectedFirmwareType) return []
      return this.firmwareList
        .filter(fw => (fw.firmwareType || '未分类') === this.selectedFirmwareType)
        .map((fw, index) => ({
          ...fw,
          _key: fw.id || `${this.selectedFirmwareType}-${fw.version || 'unknown'}-${index}`
        }))
        .sort((a, b) => {
          if (!!a.isLatest !== !!b.isLatest) return a.isLatest ? -1 : 1
          const at = a.createdAt ? new Date(a.createdAt).getTime() : 0
          const bt = b.createdAt ? new Date(b.createdAt).getTime() : 0
          if (at !== bt) return bt - at
          return String(b.version || '').localeCompare(String(a.version || ''))
        })
    },
    selectedFirmware () {
      if (!this.selectedFirmwareKey) return null
      return this.firmwareOptions.find(fw => fw._key === this.selectedFirmwareKey) || null
    },
    selectedFirmwareManifest () {
      if (!this.selectedFirmware) return ''
      return this.manifestUrlForFirmware(this.selectedFirmware)
    },
    selectedFirmwareHasFile () {
      if (!this.selectedFirmware) return null
      if (typeof this.selectedFirmware.hasFile === 'boolean') return this.selectedFirmware.hasFile
      return true
    },
    selectedFirmwareDownloadUrl () {
      if (!this.selectedFirmware) return ''
      if (this.selectedFirmware.downloadUrl) return this.selectedFirmware.downloadUrl
      if (this.selectedFirmware.id) return `/api/firmware/${this.selectedFirmware.id}/download`
      return ''
    },
    flashStatusTitle () {
      if (this.flashState === 'idle') return '等待开始'
      if (this.flashState === 'running') return '正在烧录'
      if (this.flashState === 'success') return '烧录完成'
      if (this.flashState === 'error') return '烧录失败'
      return '烧录状态更新中'
    },
    flashStatusSub () {
      if (this.flashStateDetail) return this.flashStateDetail
      if (this.flashState === 'idle') return '请在上一步选择固件并点击烧录'
      if (this.flashState === 'running') return '烧录进行中，请保持页面打开'
      if (this.flashState === 'success') return '可进入配置步骤进行参数设置'
      if (this.flashState === 'error') return '请查看日志或重新尝试烧录'
      return ''
    },
    flashProgressText () {
      if (!this.flashRunning) return ''
      return `${Math.round(this.flashProgress)}%`
    },
    configEmptyText () {
      if (!this.isConnected) return '请先连接设备后扫描配置。'
      if (this.configLoading) return '正在扫描配置...'
      return '暂无配置项，请点击“扫描配置”。'
    },
    configDebugText () {
      return (this.configDebugLines || []).join('\n')
    },
    groupedConfigItems () {
      const list = Array.isArray(this.configItems) ? this.configItems : []
      if (!list.length) return []

      const groups = new Map()
      for (const item of list) {
        if (!item || !item.name) continue
        const name = String(item.name)
        const idx = name.indexOf('_')
        const key = idx > 0 ? name.slice(0, idx) : 'base'
        if (!groups.has(key)) groups.set(key, [])
        groups.get(key).push(item)
      }

      const keyWeight = (key) => {
        if (key === 'base') return 0
        if (key === 'info') return 1
        if (key === 'wifi') return 1
        if (key === 'ros2') return 2
        return 10
      }
      const titleForKey = (key) => {
        if (key === 'base') return '基础'
        if (key === 'info') return '信息'
        return key
      }

      const out = Array.from(groups.entries())
        .map(([key, items]) => {
          const sorted = items.slice().sort((a, b) => String(a.name).localeCompare(String(b.name)))
          return { key, title: titleForKey(key), items: sorted }
        })
        .sort((a, b) => {
          const wa = keyWeight(a.key)
          const wb = keyWeight(b.key)
          if (wa !== wb) return wa - wb
          return String(a.key).localeCompare(String(b.key))
        })

      return out
    }
  },

  watch: {
    'logLines.length' () {
      if (this.activeStep !== 3) return
      this.$nextTick(() => this.scrollFlashLogToBottom())
    },
    activeStep (val) {
      if (val !== 3) return
      this.$nextTick(() => this.scrollFlashLogToBottom())
    },
    deviceId (val) {
    },
    'configDebugLines.length' () {
      if (this.activeStep !== 4) return
      this.$nextTick(() => this.scrollConfigLogToBottom())
    }
  },
  mounted () {
    const support = this.checkSerialSupport()
    this.serialSupported = support.supported
    this.serialSupportHint = support.hint
  },
  beforeDestroy () {
    this.stopConsole()
    this.disconnectDevice()
  },
  methods: {
    appendConfigDebug (dir, text) {
      const now = new Date()
      const ts = now.toISOString().slice(11, 23)
      const safeDir = dir === 'RX' ? 'RX' : 'TX'
      const line = `[${ts}] ${safeDir}> ${String(text == null ? '' : text)}`
      this.configDebugLines.push(line)
      if (this.configDebugLines.length > 800) {
        this.configDebugLines.splice(0, this.configDebugLines.length - 800)
      }
    },
    clearConfigDebug () {
      this.configDebugLines = []
    },
    scrollConfigLogToBottom () {
      try {
        const el = this.$refs.configLogBody
        if (el && el.scrollHeight != null) {
          el.scrollTop = el.scrollHeight
        }
      } catch (e) {
      }
    },
    scrollFlashLogToBottom () {
      const el = this.$refs.flashLogBody
      if (!el) return
      el.scrollTop = el.scrollHeight
    },
    checkSerialSupport () {
      if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return { supported: false, hint: '当前环境无法访问浏览器 API。' }
      }
      const isSecure = !!window.isSecureContext
      const hasSerial = !!navigator.serial
      if (!isSecure) {
        return { supported: false, hint: '当前页面不是 HTTPS/localhost，Web Serial 需要安全上下文。' }
      }
      if (!hasSerial) {
        return { supported: false, hint: '未检测到 Web Serial 接口，可能被权限策略禁用或浏览器未开启该功能。' }
      }
      return { supported: true, hint: '' }
    },
    onDeviceIdDigitInput (index, event) {
      const value = String(event && event.target ? event.target.value : '')
      const digit = value.replace(/\D/g, '').slice(0, 1)
      this.$set(this.deviceIdDigits, index, digit)
      if (event && event.target) event.target.value = digit
      if (digit && index < this.deviceIdDigits.length - 1) {
        this.focusDeviceDigit(index + 1)
      }
      this.updateDeviceIdFromDigits()
    },
    onDeviceIdDigitKeydown (index, event) {
      if (!event) return
      if (event.key === 'Backspace') {
        const current = this.deviceIdDigits[index]
        if (!current && index > 0) {
          this.focusDeviceDigit(index - 1)
        }
        return
      }
      if (event.key === 'ArrowLeft' && index > 0) {
        this.focusDeviceDigit(index - 1)
      }
      if (event.key === 'ArrowRight' && index < this.deviceIdDigits.length - 1) {
        this.focusDeviceDigit(index + 1)
      }
    },
    focusDeviceDigit (index) {
      const refs = this.$refs.deviceDigit
      if (!refs || !refs.length) return
      const el = refs[index]
      if (el && el.focus) el.focus()
    },
    updateDeviceIdFromDigits () {
      const digits = this.deviceIdDigits.join('')
      this.deviceId = digits
      if (digits.length === 8) {
        if (digits !== this.lastAutoDeviceId) this.autoFetchByDeviceId(digits)
      } else {
        this.lastAutoDeviceId = ''
      }
    },
    onFirmwareTypeChange () {
      this.selectedFirmwareKey = ''
      this.applyFirmwareDefaults()
    },
    applyFirmwareDefaults () {
      if (!this.selectedFirmwareType) {
        if (this.firmwareTypes.length) this.selectedFirmwareType = this.firmwareTypes[0]
      } else if (!this.firmwareTypes.includes(this.selectedFirmwareType)) {
        this.selectedFirmwareType = this.firmwareTypes[0] || ''
      }
      if (!this.selectedFirmwareType) return
      if (!this.selectedFirmwareKey) {
        const first = this.firmwareOptions[0]
        if (first && first._key) this.selectedFirmwareKey = first._key
      }
    },
    firmwareOptionLabel (fw) {
      const version = fw && fw.version ? fw.version : '-'
      const latest = fw && fw.isLatest ? ' · latest' : ''
      return `${version}${latest}`
    },
    appendLog (line) {
      const ts = new Date().toISOString().slice(11, 19)
      this.logLines.push(`[${ts}] ${line}`)
    },
    clearLog () {
      this.logLines = []
    },
    normalizeDeviceInfo (deviceId, raw) {
      const data = (raw && raw.data) ? raw.data : raw
      return {
        deviceId,
        deviceType: data && (data.deviceType || data.device_type || data.type),
        hardwareVersion: data && (data.hardwareVersion || data.hardware_version || data.hwVersion || data.hw_version),
        chipFamily: data && (data.chipFamily || data.chip_family || data.chip || data.chipType),
        model: data && (data.model || data.productModel || data.product_model || data.board)
      }
    },
    normalizeFirmwareInfo (raw) {
      const data = (raw && raw.data) ? raw.data : raw
      return {
        name: data && (data.name || data.firmwareName || data.firmware_name),
        version: data && (data.version || data.firmwareVersion || data.firmware_version),
        hardwareVersion: data && (data.hardwareVersion || data.hardware_version || data.hwVersion || data.hw_version),
        hardwareVersions: data && (data.hardwareVersions || data.hardware_versions),
        firmwareType: data && (data.firmwareType || data.firmware_type),
        downloadUrl: data && (data.downloadUrl || data.url || data.download_url),
        manifestUrl: data && (data.manifestUrl || data.manifest_url),
        releaseNotes: data && (data.releaseNotes || data.notes || data.release_notes)
      }
    },
    formatHardwareVersions (fw) {
      const list = Array.isArray(fw && fw.hardwareVersions) && fw.hardwareVersions.length
        ? fw.hardwareVersions
        : (fw && fw.hardwareVersion ? [fw.hardwareVersion] : [])
      return list.length ? list.join('、') : '-'
    },
    async autoFetchByDeviceId (deviceId) {
      if (this.autoFetching) return
      this.autoFetching = true
      try {
        await this.queryHardware()
        if (this.deviceInfo) {
          await this.queryFirmwareList()
        }
      } finally {
        this.autoFetching = false
        this.lastAutoDeviceId = deviceId
      }
    },
    async connectDevice () {
      const support = this.checkSerialSupport()
      this.serialSupported = support.supported
      this.serialSupportHint = support.hint
      if (!support.supported) {
        const hint = support.hint ? `（${support.hint}）` : ''
        this.$message.error(`当前环境未检测到 Web Serial${hint}`)
        return
      }
        this.connecting = true
      try {
        this.appendLog('请求串口权限...')
        const filters = Array.isArray(this.serialFilters) ? this.serialFilters : []
        const port = filters.length
          ? await navigator.serial.requestPort({ filters })
          : await navigator.serial.requestPort()
        this.port = port
        this.appendLog('已授权串口（未打开）')
        this.$message.success('连接成功')
      } catch (e) {
        this.appendLog(`连接失败：${e.message || e}`)
        this.$message.error(`连接失败：${e.message || e}`)
      } finally {
        this.connecting = false
      }
    },
    async ensurePortOpen (baudRate) {
      if (!this.port) throw new Error('no port')
      if (this.isPortOpen) return
      try {
        await this.port.open({ baudRate: Number(baudRate) || 115200 })
      } catch (e) {
        const msg = String((e && e.message) ? e.message : e)
        // Avoid "The port is already open" race when switching modes.
        if (msg.includes('already open')) return
        throw e
      }
    },
    async disconnectDevice () {
      if (!this.port) return
      try {
        this.stopConsole()
        // Best-effort: close after stopping readers.
        if (this.isPortOpen) await this.port.close()
      } catch (e) {
      } finally {
        this.port = null
      }
    },
    async queryHardware () {
      if (!this.isDeviceIdValid) {
        this.$message.warning('请输入 8 位数字设备 ID')
        return
      }
      this.loadingDevice = true
      this.deviceInfo = null
      this.firmwareInfo = null
      try {
        this.appendLog(`查询 WMS 硬件信息：deviceId=${this.deviceId}`)
        const raw = await getWmsDeviceHardware(this.deviceId)
        this.deviceInfo = this.normalizeDeviceInfo(this.deviceId, raw)
        if (!this.deviceInfo.hardwareVersion) {
          this.appendLog('已返回设备信息，但未找到硬件版本字段（请检查 WMS 返回字段名）')
          this.$message.warning('WMS 返回缺少硬件版本字段')
        } else {
          this.appendLog(`硬件版本：${this.deviceInfo.hardwareVersion}`)
        }
      } catch (e) {
        this.appendLog(`查询失败：${e.message || e}`)
        this.$message.error(`查询失败：${e.message || e}`)
      } finally {
        this.loadingDevice = false
      }
    },
    async queryFirmwareList () {
      if (!this.isDeviceIdValid) {
        this.$message.warning('请输入 8 位数字设备 ID')
        return
      }
      this.loadingFirmware = true
      this.firmwareList = []
      this.firmwareInfo = null
      this.selectedFirmwareType = ''
      this.selectedFirmwareKey = ''
      try {
        this.appendLog(`查询支持的固件列表：deviceId=${this.deviceId}`)
        const raw = await getFirmwareListByDeviceId(this.deviceId)
        const data = raw && raw.data ? raw.data : raw
        if (data && data.device) {
          this.deviceInfo = this.normalizeDeviceInfo(this.deviceId, data.device)
        }
        this.firmwareList = Array.isArray(data && data.firmwares) ? data.firmwares : []
        if (this.firmwareList.length === 0) {
          this.appendLog('未找到可用固件')
        } else {
          this.appendLog(`找到固件数量：${this.firmwareList.length}`)
        }
        this.applyFirmwareDefaults()
      } catch (e) {
        this.appendLog(`固件列表获取失败：${e.message || e}`)
        this.$message.error(`固件列表获取失败：${e.message || e}`)
      } finally {
        this.loadingFirmware = false
      }
    },
    async queryLatestFirmware () {
      const hw = this.deviceHardwareVersion
      if (!hw) {
        this.$message.warning('请先获取硬件版本')
        return
      }
      this.loadingFirmware = true
      this.firmwareInfo = null
      try {
        const dt = this.deviceInfo && this.deviceInfo.deviceType
        if (!dt) {
          this.$message.warning('缺少设备类型（deviceType）')
          return
        }
        this.appendLog(`查询最新固件：deviceType=${dt}, hardwareVersion=${hw}`)
        const raw = await getLatestFirmware(dt, hw)
        this.firmwareInfo = this.normalizeFirmwareInfo(raw)
        if (this.firmwareInfo && this.firmwareInfo.version) {
          this.appendLog(`匹配到固件版本：${this.firmwareInfo.version}`)
        } else {
          this.appendLog('已返回固件信息，但缺少 version 字段（请检查后台返回字段名）')
        }
      } catch (e) {
        this.appendLog(`固件匹配失败：${e.message || e}`)
        this.$message.error(`固件匹配失败：${e.message || e}`)
      } finally {
        this.loadingFirmware = false
      }
    },
    async flashLatest () {
      if (!this.isConnected) {
        this.$message.warning('请先连接设备')
        return
      }
      if (!this.isDeviceIdValid) {
        this.$message.warning('请输入 8 位数字设备 ID')
        return
      }

      this.flashing = true
      try {
        await this.ensurePortOpen(this.flashingBaudrate)
        if (!this.deviceInfo) await this.queryHardware()
        if (this.deviceInfo && this.deviceInfo.hardwareVersion && !this.firmwareInfo) {
          await this.queryLatestFirmware()
        }

        if (!this.firmwareInfo || !this.firmwareInfo.downloadUrl) {
          this.appendLog('未拿到可用的固件下载地址；烧录流程需要后台返回 downloadUrl/manifestUrl')
          this.$message.warning('固件信息不完整，无法开始烧录')
          return
        }

        this.appendLog('已完成固件匹配：准备进入烧录流程')
        this.appendLog(`固件下载地址：${this.firmwareInfo.downloadUrl}`)

        this.$message.success('已匹配最新固件，请在固件列表中点击“烧录”开始刷写')
      } finally {
        this.flashing = false
      }
    },
    manifestUrlForFirmware (fw) {
      if (!fw) return ''
      if (fw.manifestUrl) return fw.manifestUrl
      if (fw.id) return `/api/firmware/${fw.id}/manifest`
      return ''
    },
    async startFlash (fw) {
      if (!fw) return
      if (fw && typeof fw.hasFile === 'boolean' && !fw.hasFile) {
        this.$message.warning('该固件没有可用文件，无法烧录')
        return
      }
      const downloadUrl = this.selectedFirmwareDownloadUrl
      if (!downloadUrl) {
        this.$message.warning('固件下载地址为空')
        return
      }
      this.flashRunning = true
      this.flashState = 'running'
      this.flashProgress = 0
      this.flashStateDetail = `固件 ${fw.version || fw.title || fw.name || ''}`.trim()
      this.activeStep = 3
      let transport = null
      try {
        await this.stopConsole()
        await this.closePortIfOpen()
        const { ESPLoader, Transport } = await this.loadEsptoolModule()
        let port = this.port
        if (!port) {
          this.appendLog('请求串口权限...')
          port = await navigator.serial.requestPort({ filters: this.serialFilters || [] })
          this.port = port
        } else {
          this.appendLog('复用已连接的串口设备')
        }

        this.appendLog('开始下载固件...')
        const res = await fetch(downloadUrl)
        if (!res.ok) throw new Error(`固件下载失败：HTTP ${res.status}`)
        const buffer = await res.arrayBuffer()
        const dataU8 = new Uint8Array(buffer)
        this.appendLog(`固件大小：${dataU8.length} bytes`)

        transport = new Transport(port, false, true)
        this.patchTransportConnect(transport)
        const terminal = {
          clean: () => {},
          writeLine: (line) => this.appendLog(String(line || '')),
          write: (line) => this.appendLog(String(line || ''))
        }
        const loader = new ESPLoader({
          transport,
          baudrate: this.flashingBaudrate || 115200,
          terminal
        })

        await loader.main()

        const imageBstr = this.ui8ToBstr(dataU8)

        const flashOptions = {
          // esptool-js@0.5.7 expects binary-string (not Uint8Array)
          fileArray: [{ address: 0x0, data: imageBstr }],
          flashMode: 'keep',
          flashFreq: 'keep',
          flashSize: 'keep',
          eraseAll: false,
          // Non-compressed writes are not implemented in this version
          compress: true,
          reportProgress: (fileIndex, written, total) => {
            if (!total) return
            this.flashProgress = Math.min(100, (written / total) * 100)
          }
        }

        await loader.writeFlash(flashOptions)
        await loader.after('hard_reset')

        this.flashState = 'success'
        this.flashProgress = 100
        this.appendLog('烧录完成')
        this.activeStep = 4
      } catch (e) {
        const message = e && e.message ? e.message : e
        this.flashState = 'error'
        this.flashStateDetail = message
        this.appendLog(`烧录失败：${message}`)
        if (e && e.stack) this.appendLog(String(e.stack))
        if (String(message).includes('Failed to connect with the device')) {
          this.$message.error('烧录失败：无法连接设备（请尝试让设备进入下载模式后重试）')
          this.appendLog('提示：部分设备需要按住 BOOT，再按一下 RESET 进入下载模式，然后重新点击“烧录”。')
        } else {
          this.$message.error(`烧录失败：${message}`)
        }
      } finally {
        if (transport) {
          try {
            await transport.disconnect()
          } catch (e) {
          }
        }
        this.flashRunning = false
      }
    },
    patchTransportConnect (transport) {
      if (!transport || transport._patchedConnect) return
      transport._patchedConnect = true
      const originalConnect = transport.connect.bind(transport)
      transport.connect = async (baud = 115200, serialOptions = {}) => {
        try {
          return await originalConnect(baud, serialOptions)
        } catch (e) {
          const message = e && e.message ? e.message : String(e)
          if (message.includes('already open')) {
            // If browser considers it open, just reuse and start reader.
            transport.baudrate = baud
            try {
              const ret = transport.readLoop()
              if (ret && typeof ret.catch === 'function') ret.catch(() => {})
            } catch (e) {
            }
            return
          }
          throw e
        }
      }
    },
    async loadEsptoolModule () {
      if (this.esptoolModule) return this.esptoolModule
      this.installGlobalErrorHandler()
      const mod = await import(/* webpackIgnore: true */ 'https://unpkg.com/esptool-js@0.5.7/bundle.js')
      if (!mod || !mod.ESPLoader || !mod.Transport) {
        throw new Error('esptool-js 未暴露 ESPLoader/Transport')
      }
      this.esptoolModule = { ESPLoader: mod.ESPLoader, Transport: mod.Transport }
      return this.esptoolModule
    },
    installGlobalErrorHandler () {
      if (this.errorHandlerInstalled || typeof window === 'undefined') return
      this.errorHandlerInstalled = true
      window.addEventListener('error', (event) => {
        if (!event) return
        const filename = event.filename || ''
        if (filename.includes('esptool-js')) {
          const message = event.message || '脚本异常'
          this.appendLog(`esptool-js 异常：${message}`)
        }
      })
    },
    async closePortIfOpen () {
      if (!this.port) return
      if (this.isPortOpen) {
        try {
          await this.port.close()
        } catch (e) {
        }
      }
    },
    async writeToSerial (text) {
      if (!this.port || !this.port.writable) throw new Error('串口不可写')
      const writer = this.port.writable.getWriter()
      try {
        const encoder = new TextEncoder()
        await writer.write(encoder.encode(text))
      } finally {
        writer.releaseLock()
      }
    },
    async readSerialFor (durationMs) {
      if (!this.port || !this.port.readable) return ''
      const chunks = []
      const aborter = new AbortController()
      const textDecoder = new TextDecoderStream()
      const readableStreamClosed = this.port.readable.pipeTo(textDecoder.writable, { signal: aborter.signal }).catch(() => {})
      const reader = textDecoder.readable.getReader()
      const timeout = setTimeout(() => aborter.abort(), durationMs)
      try {
        while (true) {
          const { value, done } = await reader.read()
          if (done) break
          if (value) chunks.push(value)
        }
        await readableStreamClosed
      } catch (e) {
      } finally {
        clearTimeout(timeout)
        try {
          reader.releaseLock()
        } catch (e) {
        }
      }
      return chunks.join('')
    },
    parseConfigLines (text) {
      if (!text) return []
      return text
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(line => line.startsWith('$') && line.includes('='))
        .map((line) => {
          const body = line.slice(1)
          const idx = body.indexOf('=')
          const name = body.slice(0, idx).trim()
          const value = body.slice(idx + 1).trim()
          return { name, value, writing: false }
        })
        .filter(item => item.name)
    },
    ui8ToBstr (u8) {
      if (!u8 || !u8.length) return ''
      const chunkSize = 0x8000
      const parts = []
      for (let i = 0; i < u8.length; i += chunkSize) {
        const chunk = u8.subarray(i, i + chunkSize)
        // eslint-disable-next-line prefer-spread
        parts.push(String.fromCharCode.apply(null, chunk))
      }
      return parts.join('')
    },
    async readConfig () {
      if (!this.port) {
        this.$message.warning('请先连接设备')
        return
      }
      this.configLoading = true
      this.configItems = []
      try {
        this.stopConsole()
        await this.ensurePortOpen(this.consoleBaudrate)
        const cmd = '$command=read_config\n'
        this.appendConfigDebug('TX', cmd)
        await this.writeToSerial(cmd)
        this.appendLog('已发送 read_config，等待返回...')
        const raw = await this.readSerialFor(1000)
        if (raw) this.appendConfigDebug('RX', raw)
        const items = this.parseConfigLines(raw)
        this.configItems = items
        if (items.length === 0) {
          this.$message.warning('未读取到配置项')
        } else {
          this.appendLog(`读取到配置项：${items.length}`)
        }
      } catch (e) {
        this.$message.error(`扫描配置失败：${e.message || e}`)
        this.appendLog(`扫描配置失败：${e.message || e}`)
        this.appendConfigDebug('RX', `ERROR: ${e.message || e}`)
      } finally {
        try {
          if (this.isPortOpen) await this.port.close()
        } catch (e) {
        }
        this.configLoading = false
      }
    },
    async writeConfigItem (item) {
      if (!this.port) {
        this.$message.warning('请先连接设备')
        return
      }
      if (!item || !item.name) return
      if (item.readOnly) {
        this.$message.info('该项为只读信息，无法写入')
        return
      }
      item.writing = true
      try {
        this.stopConsole()
        await this.ensurePortOpen(this.consoleBaudrate)
        const line = `$${item.name}=${item.value}\n`
        this.appendConfigDebug('TX', line)
        await this.writeToSerial(line)
        this.appendLog(`写入配置：${item.name}=${item.value}`)
        this.$message.success('写入成功')
      } catch (e) {
        this.$message.error(`写入失败：${e.message || e}`)
        this.appendLog(`写入失败：${e.message || e}`)
        this.appendConfigDebug('RX', `ERROR: ${e.message || e}`)
      } finally {
        try {
          if (this.isPortOpen) await this.port.close()
        } catch (e) {
        }
        item.writing = false
      }
    },
    async sendRestart () {
      if (!this.port) {
        this.$message.warning('请先连接设备')
        return
      }
      this.restartLoading = true
      try {
        this.stopConsole()
        await this.ensurePortOpen(this.consoleBaudrate)
        const cmd = '$command=restart\n'
        this.appendConfigDebug('TX', cmd)
        await this.writeToSerial(cmd)
        this.appendLog('已发送 restart 指令')
        this.$message.success('已发送重启指令')
      } catch (e) {
        this.$message.error(`重启失败：${e.message || e}`)
        this.appendLog(`重启失败：${e.message || e}`)
        this.appendConfigDebug('RX', `ERROR: ${e.message || e}`)
      } finally {
        try {
          if (this.isPortOpen) await this.port.close()
        } catch (e) {
        }
        this.restartLoading = false
      }
    },
    async startConsole () {
      if (!this.port) return
      this.consoleStarting = true
      try {
        await this.ensurePortOpen(this.consoleBaudrate)
        if (!this.port.readable) {
          this.$message.warning('串口不可读')
          return
        }
        this.consoleRunning = true
        this.appendLog('开始读取串口输出...')

        const aborter = new AbortController()
        this.consolePipeAborter = aborter

        const textDecoder = new TextDecoderStream()
        const readableStreamClosed = this.port.readable.pipeTo(textDecoder.writable, { signal: aborter.signal }).catch(() => {})
        const reader = textDecoder.readable.getReader()
        this.consoleReader = reader

        this.consoleLines.push('\n--- console start ---\n')
        while (this.consoleRunning) {
          const { value, done } = await reader.read()
          if (done) break
          if (value) {
            this.consoleLines.push(value)
            if (this.consoleLines.length > 2000) this.consoleLines.splice(0, this.consoleLines.length - 2000)
          }
        }

        await readableStreamClosed
      } catch (e) {
        this.appendLog(`控制台读取异常：${e.message || e}`)
      } finally {
        this.consoleStarting = false
      }
    },
    stopConsole () {
      this.consoleRunning = false
      try {
        if (this.consoleReader) {
          this.consoleReader.cancel().catch(() => {})
          this.consoleReader.releaseLock()
        }
      } catch (e) {
      }
      this.consoleReader = null

      try {
        if (this.consolePipeAborter) this.consolePipeAborter.abort()
      } catch (e) {
      }
      this.consolePipeAborter = null
    },
    async resetDevice () {
      if (!this.port || !this.port.setSignals) {
        this.$message.warning('当前环境不支持信号线控制')
        return
      }
      try {
        if (!this.isPortOpen) await this.ensurePortOpen(this.consoleBaudrate)
        // 常见 ESP32 自动复位线序：RTS/DTR 组合。不同 USB 转串口芯片可能表现不同。
        await this.port.setSignals({ dataTerminalReady: false, requestToSend: true })
        await new Promise(resolve => setTimeout(resolve, 120))
        await this.port.setSignals({ dataTerminalReady: true, requestToSend: false })
        this.$message.success('已发送重启信号')
      } catch (e) {
        this.$message.error(`重启失败：${e.message || e}`)
      }
    }
  }
}
</script>

<style scoped>
.launchpad {
  --bg0: #eef3f6;
  --bg1: #e9f7f5;
  --card: #ffffff;
  --ink: #17212b;
  --muted: #5f6b76;
  --teal: #178da3;
  --teal2: #0ea5a7;
  --line: rgba(23, 33, 43, 0.08);
  width: 88%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 0 40px;
  color: var(--ink);
}

.lead {
  margin: 0;
  color: #666;
  line-height: 1.7;
}

.note {
  margin: 8px 0 0;
  color: rgba(15, 23, 42, 0.6);
  font-size: 12px;
}


.status-card {
  border-radius: 14px;
  border: 1px solid var(--line);
}

.status-row {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(23, 33, 43, 0.15);
}

.dot.on {
  background: rgba(14, 165, 167, 1);
  box-shadow: 0 0 0 4px rgba(14, 165, 167, 0.18);
}

.status-title {
  font-weight: 700;
}

.status-sub {
  margin-top: 2px;
  color: var(--muted);
  font-size: 12px;
}

.status-actions {
  margin-top: 14px;
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}

.status-warn {
  margin-top: 12px;
  font-size: 12px;
  color: #a15500;
  background: rgba(255, 210, 122, 0.25);
  border: 1px solid rgba(255, 210, 122, 0.55);
  padding: 8px 10px;
  border-radius: 10px;
}


.panel {
  margin-top: 0;
  padding: 22px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #ececec;
}

.panel-head {
  padding: 0 6px 14px;
  text-align: center;
}

.panel-head h1 {
  margin: 0 0 8px;
  color: #1f2b5b;
  font-size: 32px;
  line-height: 1.25;
}

.panel-head p {
  margin: 0;
  color: #666;
}

.subhead {
  margin: 0 0 10px;
}


.stepper {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  padding: 0 6px;
  justify-content: center;
  margin-top: 6px;
}

.stepper-item {
  appearance: none;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.85);
  border-radius: 999px;
  padding: 8px 12px;
  color: var(--ink);
  cursor: pointer;
  font-weight: 700;
}

.stepper-item.on {
  border-color: rgba(23, 141, 163, 0.35);
  background: rgba(23, 141, 163, 0.1);
  color: rgba(23, 141, 163, 1);
}

.stepper-item[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}

.step-wrap {
  display: grid;
  gap: 14px;
  width: 100%;
  max-width: 1060px;
  margin: 10px auto 0;
}

.step-card {
  width: 100%;
}

.step-card ::v-deep .el-card__body {
  padding: 18px;
}

.grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr;
  gap: 12px;
}

.settings-grid {
  grid-template-columns: 1fr 1fr;
}

.card {
  border: 1px solid var(--line);
  border-radius: 14px;
}

.card-title {
  font-weight: 800;
  margin-bottom: 12px;
}

.step-subtitle {
  margin-top: 14px;
  font-weight: 700;
  font-size: 13px;
}

.field {
  margin-top: 8px;
}

.field-label {
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 6px;
}

.field-help {
  margin-top: 6px;
  font-size: 12px;
  color: var(--muted);
}

.field-help.ok {
  color: rgba(14, 165, 167, 1);
}

.field-help.bad {
  color: #b42318;
}

.device-id-row {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: nowrap;
  justify-content: center;
}

.digit-input {
  width: 44px;
  height: 48px;
  border: 1px solid rgba(23, 33, 43, 0.26);
  border-radius: 10px;
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.95);
  color: var(--ink);
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.digit-input:focus {
  border-color: rgba(17, 24, 39, 0.55);
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.12);
}

.digit-sep {
  font-weight: 700;
  color: var(--muted);
  margin: 0 4px;
}

.card-actions {
  margin-top: 14px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

.mini-warn {
  margin-top: 10px;
  font-size: 12px;
  color: #7a3d00;
}

.kv {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.kv-row {
  display: grid;
  grid-template-columns: 86px 1fr;
  gap: 10px;
  align-items: baseline;
}

.kv-row span {
  font-size: 12px;
  color: var(--muted);
}

.kv-row b {
  font-weight: 700;
}

.kv-row b.wrap {
  white-space: pre-wrap;
  word-break: break-word;
  font-weight: 600;
}

.result {
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.7);
  border-radius: 14px;
  padding: 12px;
}

.result-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.result-title {
  font-weight: 800;
}

.result-sub {
  font-size: 12px;
  color: var(--muted);
}

.result-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.result-item {
  border: 1px solid rgba(23, 33, 43, 0.06);
  border-radius: 12px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.65);
}

.result-label {
  font-size: 12px;
  color: var(--muted);
}

.result-value {
  margin-top: 4px;
  font-weight: 700;
  word-break: break-word;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.empty {
  color: var(--muted);
  font-size: 13px;
}

.log {
  margin-top: 12px;
  border: 1px solid var(--line);
  border-radius: 14px;
}

.log-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 10px;
}

.log-sub {
  margin-left: 8px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 400;
}

.log-body {
  margin: 0;
  padding: 12px;
  height: 220px;
  overflow: auto;
  background: #0e1216;
  color: #e7f0f2;
  border-radius: 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  line-height: 1.6;
}

.flash-status {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
}

.config-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.config-group {
  margin-top: 14px;
}

.config-group:first-child {
  margin-top: 0;
}

.config-group-title {
  text-align: left;
  font-weight: 900;
  color: var(--ink);
  font-size: 13px;
  letter-spacing: 0.02em;
  margin: 6px 2px;
}

.config-item {
  display: grid;
  grid-template-columns: 160px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 10px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.7);
}

.config-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-width: 56px;
}

.ro {
  font-size: 12px;
  font-weight: 800;
  color: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(15, 23, 42, 0.14);
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.02);
}

.config-name {
  font-size: 15px;
  font-weight: 800;
  color: var(--ink);
  word-break: break-all;
}

.fw-list {
  display: grid;
  gap: 10px;
}

.fw-picker {
  display: grid;
  gap: 12px;
}

.fw-picker-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 12px;
  align-items: end;
}

.fw-picker-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
  margin-top: 22px;
}

.fw-picker .el-select {
  width: 100%;
}

.fw-detail {
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.7);
}

.fw-group-list {
  display: grid;
  gap: 14px;
}

.fw-group {
  display: grid;
  gap: 8px;
}

.fw-group-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--ink);
}

.fw-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.7);
}

.fw-main {
  flex: 1;
  min-width: 0;
}

.fw-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.fw-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(14, 165, 167, 0.14);
  color: rgba(14, 165, 167, 1);
  border: 1px solid rgba(14, 165, 167, 0.25);
}

.fw-sub {
  margin-top: 6px;
  font-size: 12px;
  color: var(--muted);
}

.fw-notes {
  margin-top: 8px;
  font-size: 12px;
  color: var(--muted);
  white-space: pre-wrap;
  word-break: break-word;
}

.fw-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
}

.fw-actions a {
  font-size: 12px;
  color: rgba(23, 141, 163, 1);
  text-decoration: underline;
}

.fw-flash {
  appearance: none;
  border: 1px solid rgba(23, 141, 163, 0.35);
  background: rgba(23, 141, 163, 0.1);
  color: rgba(23, 141, 163, 1);
  border-radius: 999px;
  padding: 8px 14px;
  cursor: pointer;
  font-weight: 700;
}

.fw-flash[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}

.about {
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: var(--muted);
}

.about-row b {
  color: var(--ink);
}

@media screen and (max-width: 1020px) {
  .panel {
    padding: 16px;
  }
  .device-id-row {
    flex-wrap: wrap;
    row-gap: 10px;
  }
  .digit-input {
    width: 38px;
    height: 44px;
  }
  .config-item {
    grid-template-columns: 1fr;
  }
  .result-grid {
    grid-template-columns: 1fr;
  }
  .fw-picker-row {
    grid-template-columns: 1fr;
  }
  .fw-picker-actions {
    margin-top: 6px;
  }
}

@media screen and (max-width: 768px) {
  .launchpad {
    width: 90%;
  }

  .config-list {
    grid-template-columns: 1fr;
  }

  .panel-head h1 {
    font-size: 26px;
  }
}
</style>
el-head h1 {
    font-size: 26px;
  }
}
</style>
